import { resolvePostActivitySelectorPrefix } from './tours/post-activity.tour';
import { delay } from './shepherd.utils';

export const POST_ACTIVITY_LOCATION_STEP_ID = 'post-activity-location';

/** Shepherd step id → `data-tour` attribute on the highlighted element. */
export const POST_ACTIVITY_STEP_TOUR_ATTR: Record<string, string> = {
  'post-activity-type': 'post-activity-activity-type',
  [POST_ACTIVITY_LOCATION_STEP_ID]: POST_ACTIVITY_LOCATION_STEP_ID,
  'post-activity-skill': 'post-activity-skill-level',
  'post-activity-create': 'post-activity-create-button',
};

/** Tooltip placement per step — used to reserve space when scrolling the modal. */
export const POST_ACTIVITY_STEP_PLACEMENT: Record<string, 'top' | 'bottom'> = {
  'post-activity-type': 'bottom',
  [POST_ACTIVITY_LOCATION_STEP_ID]: 'bottom',
  'post-activity-skill': 'top',
  'post-activity-create': 'top',
};

/** While the post-activity Shepherd tour runs — modal under overlay, dim outside cutout. */
export const POST_ACTIVITY_TOUR_BODY_CLASS = 'shepherd-post-activity-tour-active';

/** Location step: autocomplete + dull surround while tip is open. */
export const POST_ACTIVITY_LOCATION_INTERACTION_CLASS = 'shepherd-post-activity-location-interaction';

/** Location step: fixed dim when modal SVG overlay is paused (mobile keyboard). */
export const POST_ACTIVITY_LOCATION_SPOTLIGHT_CLASS = 'shepherd-post-activity-location-tour-spotlight';

export function setPostActivityTourBodyActive(active: boolean): void {
  document.body.classList.toggle(POST_ACTIVITY_TOUR_BODY_CLASS, active);
  if (!active) {
    clearPostActivityLocationTourUi();
  }
}

export function setPostActivityLocationInteractionActive(active: boolean): void {
  document.body.classList.toggle(POST_ACTIVITY_LOCATION_INTERACTION_CLASS, active);
}

export function setPostActivityLocationSpotlightActive(active: boolean): void {
  document.body.classList.toggle(POST_ACTIVITY_LOCATION_SPOTLIGHT_CLASS, active);
}

export function clearPostActivityLocationTourUi(): void {
  setPostActivityLocationInteractionActive(false);
  setPostActivityLocationSpotlightActive(false);
  resetPostActivityLocationTargetPadding();
}

export function resolvePostActivityLocationTarget(): HTMLElement | null {
  const prefix = resolvePostActivitySelectorPrefix();
  return document.querySelector(`${prefix}[data-tour="${POST_ACTIVITY_LOCATION_STEP_ID}"]`);
}

/** Clears legacy padding used before overlay passthrough handled dropdown clicks. */
export function resetPostActivityLocationTargetPadding(): void {
  const root = resolvePostActivityLocationTarget();
  if (root) {
    root.style.paddingBottom = '';
  }
}

function queryHTMLElement(selector: string): HTMLElement | null {
  const el = document.querySelector(selector);
  return el instanceof HTMLElement ? el : null;
}

/** Scrollable modal body while the post-activity tour runs (step 2 details form). */
export function getPostActivityModalScroller(): HTMLElement | null {
  const prefix = resolvePostActivitySelectorPrefix().trim();
  if (prefix) {
    const scoped = queryHTMLElement(`${prefix} .create-golf-event-content`);
    if (scoped) {
      return scoped;
    }
  }
  return (
    queryHTMLElement('.create-golf-event-modal .create-golf-event-content') ??
    queryHTMLElement('.activity-post .create-golf-event-content')
  );
}

export type PostActivityModalScrollOptions = {
  placement?: 'top' | 'bottom';
  smooth?: boolean;
};

/** Scroll modal content so a tour target is visible without using document scrollIntoView. */
export function scrollPostActivityModalToTarget(
  target: HTMLElement | null,
  options?: PostActivityModalScrollOptions,
): void {
  if (!target) {
    return;
  }
  const scroller = getPostActivityModalScroller();
  if (!scroller) {
    return;
  }
  const smooth = options?.smooth !== false;
  const placement = options?.placement ?? 'bottom';
  const tipReserve = 120;
  const edgePadding = 24;
  const reserveTop = placement === 'top' ? tipReserve + edgePadding : edgePadding;
  const reserveBottom = placement === 'bottom' ? tipReserve + edgePadding : edgePadding;

  const apply = (): void => {
    const scrollerRect = scroller.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    let delta = 0;
    if (targetRect.top < scrollerRect.top + reserveTop) {
      delta = targetRect.top - scrollerRect.top - reserveTop;
    } else if (targetRect.bottom > scrollerRect.bottom - reserveBottom) {
      delta = targetRect.bottom - scrollerRect.bottom + reserveBottom;
    }
    if (Math.abs(delta) < 4) {
      return;
    }
    scroller.scrollBy({ top: delta, behavior: smooth ? 'smooth' : 'auto' });
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(apply);
  });
}

export function resolvePostActivityTourTarget(stepId: string | undefined): HTMLElement | null {
  const tourAttr = stepId ? POST_ACTIVITY_STEP_TOUR_ATTR[stepId] : undefined;
  if (!tourAttr) {
    return null;
  }
  const prefix = resolvePostActivitySelectorPrefix();
  return document.querySelector(`${prefix}[data-tour="${tourAttr}"]`) as HTMLElement | null;
}

/** Scroll modal to the step target (Next / Back / show). Re-run after layout settles. */
export function scrollPostActivityModalToStep(stepId: string | undefined): void {
  if (!stepId || !POST_ACTIVITY_STEP_TOUR_ATTR[stepId]) {
    return;
  }
  const target = resolvePostActivityTourTarget(stepId);
  scrollPostActivityModalToTarget(target, {
    placement: POST_ACTIVITY_STEP_PLACEMENT[stepId],
    smooth: true,
  });
}

let postActivityModalScrollGeneration = 0;

/** Cancel pending delayed scrolls (e.g. location step Back before 120ms/350ms callbacks run). */
export function cancelPostActivityModalScrollAfterShow(): void {
  postActivityModalScrollGeneration += 1;
}

export function scrollPostActivityModalToStepAfterShow(stepId: string | undefined): void {
  const generation = ++postActivityModalScrollGeneration;
  scrollPostActivityModalToStep(stepId);
  void delay(120).then(() => {
    if (generation !== postActivityModalScrollGeneration) {
      return;
    }
    scrollPostActivityModalToStep(stepId);
  });
  void delay(350).then(() => {
    if (generation !== postActivityModalScrollGeneration) {
      return;
    }
    scrollPostActivityModalToStep(stepId);
  });
}
